package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LDisclosure
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener
import dev.lingvo.site.i18n.tf

/** Properties read only committed snapshots and submit one edit when a field commits. */
@Composable
internal fun PropertiesPanel(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    Div(attrs = { attr("data-studio-region", "properties"); tabIndex(-1); attr("aria-label", tr("studio.editor.properties_panel.properties", "Properties")) }) {
        val layer = state.selected.singleOrNull()
        if (state.selected.size > 1) Text(t("studio.editor.properties_panel.mixed", "Mixed"))
        if (state.selected.isEmpty()) {
            StudioNameStatus(t("studio.editor.properties_panel.artboard", "Artboard"),
                "${state.board.canvas.width} × ${state.board.canvas.height}") // i18n-ignore
            val background = state.layers.filterIsInstance<dev.lingvo.shared.screenshots.doc.BackgroundLayer>().firstOrNull()
            if (background != null) FillProperties(state.copy(selection = setOf(background.base.id)), dispatch)
            ScenePicker(state, dispatch)
        }
        if (layer != null) {
            StudioDisclosure(t("studio.editor.properties_panel.position", "Position")) {
                val box = layer.base.box
                StudioNumberField("X", box.x, state.editable && !layer.base.locked) { dispatch(StudioIntent.SetBox(layer.base.id, box.copy(x = it))) }
                StudioNumberField("Y", box.y, state.editable && !layer.base.locked) { dispatch(StudioIntent.SetBox(layer.base.id, box.copy(y = it))) }
                StudioNumberField(t("studio.editor.properties_panel.width", "Width"), box.w, state.editable && !layer.base.locked) {
                    dispatch(StudioIntent.SetBox(layer.base.id, box.copy(w = it, h = if (layer is DeviceLayer) it * box.h / box.w else box.h)))
                }
                StudioNumberField(t("studio.editor.properties_panel.height", "Height"), box.h, state.editable && !layer.base.locked) {
                    dispatch(StudioIntent.SetBox(layer.base.id, box.copy(h = it, w = if (layer is DeviceLayer) it * box.w / box.h else box.w)))
                }
                StudioNumberField(t("studio.editor.properties_panel.rotation", "Rotation"), box.rotation, state.editable && !layer.base.locked) {
                    dispatch(StudioIntent.SetBox(layer.base.id, box.copy(rotation = it)))
                }
            }
            TextProperties(state, dispatch)
            if (!state.localizedSelection) {
                FillProperties(state, dispatch)
                EffectProperties(state, dispatch)
                DeviceProperties(state, dispatch)
            }
            LocalizePanel(state, dispatch)
        }
    }
}

@Composable
internal fun StudioDisclosure(title: String, content: @Composable () -> Unit) {
    var expanded by remember { mutableStateOf(true) }
    LDisclosure(title, expanded, { expanded = it }, content = content)
}

@Composable
internal fun StudioButton(label: String, enabled: Boolean = true, onClick: () -> Unit) =
    LButton(label, onClick, variant = LButtonVariant.SECONDARY, enabled = enabled)

// The single swap point for W2-10's number field. Drafts commit on Enter, blur or arrow step.
@Composable
private fun StudioNumberField(label: String, value: Double, enabled: Boolean, commit: (Double) -> Unit) {
    if (!enabled) { Text(tf("studio.property_value", "{label}: {value}", "label" to label, "value" to value)); return }
    var draft by remember(value) { mutableStateOf(value.toString()) }
    val latestDraft by rememberUpdatedState(draft)
    val latestCommit by rememberUpdatedState(commit)
    fun save() { latestDraft.toDoubleOrNull()?.takeIf { it.isFinite() && it != value }?.let(latestCommit) }
    Div(attrs = {
        onKeyDown {
            when (it.key) {
                "Enter" -> { it.preventDefault(); save() }
                "Escape" -> { it.preventDefault(); draft = value.toString() }
                "ArrowUp", "ArrowDown" -> { it.preventDefault()
                    val number = (draft.toDoubleOrNull() ?: value) + if (it.key == "ArrowUp") 1 else -1
                    draft = number.toString(); latestCommit(number)
                }
            }
        }
        ref { element -> val blur = EventListener { save() }; element.addEventListener("focusout", blur)
            onDispose { element.removeEventListener("focusout", blur) } }
    }) { LTextField(draft, { draft = it }, label = label) }
}

/** All numeric properties share the single temporary text-field implementation until W2-10 lands. */
@Composable
internal fun StudioNumericProperty(label: String, value: Double, enabled: Boolean, commit: (Double) -> Unit) {
    StudioNumberField(label, value, enabled, commit)
}
